import React from 'react'
import PropTypes from 'prop-types'

/**
 * TeamRosterTable Component
 *
 * Displays team rosters with player information including jersey numbers, positions, and captain status.
 * Used in LeagueDetails (Teams tab), Dashboard (roster view), and Home (roster display).
 *
 * @param {Object} props
 * @param {Array} props.players - Array of player data
 * @param {string} [props.teamColor] - Team color for styling
 * @param {boolean} [props.showActions=false] - Show edit/remove actions
 * @param {Function} [props.onEdit] - Callback for edit action
 * @param {Function} [props.onRemove] - Callback for remove action
 * @param {boolean} [props.compact=false] - Use compact display mode
 * @param {string} [props.className=''] - Additional CSS classes
 * @param {Function} [props.onPlayerClick] - Callback when player is clicked
 * @param {string} [props.emptyMessage='No players on roster'] - Message when no players
 */
const TeamRosterTable = React.memo(({
  players = [],
  teamColor,
  showActions = false,
  onEdit,
  onRemove,
  compact = false,
  className = '',
  onPlayerClick,
  emptyMessage = 'No players on roster'
}) => {
  if (!players || players.length === 0) {
    return (
      <p className="text-slate-500 text-sm italic text-center py-4">
        {emptyMessage}
      </p>
    )
  }

  const getPositionBadge = (player) => {
    if (!player.position && !player.sub_position) return null

    let positionText = 'P'
    if (player.position === 'goalie') {
      positionText = 'G'
    } else if (player.sub_position === 'forward') {
      positionText = 'F'
    } else if (player.sub_position === 'defense') {
      positionText = 'D'
    }

    return (
      <span className="badge badge-neutral text-xs">
        {positionText}
      </span>
    )
  }

  if (compact) {
    return (
      <div className={`space-y-2 ${className}`}>
        {players.map((player) => (
          <div
            key={player.id}
            className={`flex items-center gap-2 text-sm ${
              onPlayerClick ? 'cursor-pointer hover:bg-slate-50 p-2 rounded-lg transition-colors' : ''
            }`}
            onClick={onPlayerClick ? () => onPlayerClick(player) : undefined}
          >
            {player.jersey_number && (
              <span className="text-slate-500 font-mono w-6 text-xs">
                #{player.jersey_number}
              </span>
            )}
            <span className="flex-1 text-slate-900">{player.name}</span>
            {getPositionBadge(player)}
            {player.is_captain === 1 && (
              <span className="badge badge-primary text-xs">
                C
              </span>
            )}
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className={`space-y-2 ${className}`}>
      {players.map((player) => (
        <div
          key={player.id}
          className={`p-4 bg-slate-50 rounded-lg border border-slate-100 ${
            onPlayerClick ? 'cursor-pointer hover:bg-slate-100 transition-colors' : ''
          }`}
          onClick={onPlayerClick && !showActions ? () => onPlayerClick(player) : undefined}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                {player.jersey_number && (
                  <span
                    className="flex items-center justify-center w-8 h-8 rounded-full text-white font-bold text-sm"
                    style={{ backgroundColor: teamColor || '#0284c7' }}
                  >
                    {player.jersey_number}
                  </span>
                )}
                <span className="font-medium text-slate-900">{player.name}</span>
                {player.is_captain === 1 && (
                  <span className="badge badge-primary text-xs">
                    Captain
                  </span>
                )}
              </div>

              <div className="flex items-center gap-3 text-sm text-slate-600">
                {getPositionBadge(player)}
                {player.email && (
                  <span className="text-xs truncate text-slate-500">{player.email}</span>
                )}
              </div>
            </div>

            {showActions && (onEdit || onRemove) && (
              <div className="flex gap-2 flex-shrink-0">
                {onEdit && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onEdit(player)
                    }}
                    className="btn-secondary btn-sm"
                    title="Edit player"
                  >
                    Edit
                  </button>
                )}
                {onRemove && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onRemove(player)
                    }}
                    className="btn-danger btn-sm"
                    title="Remove player"
                  >
                    Remove
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
})

TeamRosterTable.displayName = 'TeamRosterTable'

TeamRosterTable.propTypes = {
  players: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    jersey_number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    position: PropTypes.string,
    sub_position: PropTypes.string,
    email: PropTypes.string,
    is_captain: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  })),
  teamColor: PropTypes.string,
  showActions: PropTypes.bool,
  onEdit: PropTypes.func,
  onRemove: PropTypes.func,
  compact: PropTypes.bool,
  className: PropTypes.string,
  onPlayerClick: PropTypes.func,
  emptyMessage: PropTypes.string,
}

export default TeamRosterTable
